import React, { useCallback, useMemo, useRef } from 'react';

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@core-ui/Components/ui/card';
import { Button } from '@core-ui/Components/ui/button';
import { Input } from '@core-ui/Components/ui/input';
import { cn } from '@core-ui-lib/utils';

import { Download, FileText, Loader2, UploadCloud } from 'lucide-react';
import { writeErrorLog } from '@lib/logger/logError.ts';
import { writeWarningLog } from '@lib/logger/logWarning.ts';
import { writeInfoLog } from '@lib/logger/logInfo.ts';
import { writeSuccessLog } from '@lib/logger/logSuccess.tsx';
import { paintToast } from '@lib/logger/paintToast.ts';
import { LogLevels } from '@types';
import { useTranslation } from 'react-i18next';

interface UploadCsvCardProps {
  disabled?: boolean;
  listName?: string;
  onListNameChange: (v: string) => void;
  file: File | null;
  onFileChange: (f: File | null) => void;
  onDownloadTemplate: () => void;
  onUpload: () => Promise<void>;
  uploading?: boolean;
  hint?: string;
}

function isCsvFile (file: File): boolean {
  const name = (file.name ?? '').toLowerCase();
  const type = (file.type ?? '').toLowerCase();
  return name.endsWith('.csv') || type.includes('csv') || type === 'text/plain';
}

export default function UploadCsvCard (props: UploadCsvCardProps): React.JSX.Element {
  const {
    disabled = false,
    listName,
    onListNameChange,
    file,
    onFileChange,
    onDownloadTemplate,
    onUpload,
    uploading = false,
    hint,
  } = props;
  const { t } = useTranslation();

  const safeListName = listName ?? '';

  const inputRef = useRef<HTMLInputElement | null>(null);

  const canUpload = useMemo(() => {
    const titleOk = safeListName.trim() !== '';
    const fileOk = file !== null;
    return titleOk && fileOk && !disabled && !uploading;
  }, [safeListName, file, disabled, uploading]);

  const handlePickFileClick = useCallback(() => {
    if (disabled || uploading) {
      return;
    }
    inputRef.current?.click();
  }, [disabled, uploading]);

  const handleFileSelected = useCallback(
    async (f: File | null) => {
      if (f === null) {
        onFileChange(null);
        return;
      }

      if (!isCsvFile(f)) {
        onFileChange(null);
        await writeWarningLog(
          t('contacts.toast.warning.invalidFileType', { lng: 'en' }),
          'Contacts.UploadCsvCard.handleFileSelected',
          { fileName: f.name, fileType: f.type }
        );
        paintToast(LogLevels.WARNING, t('contacts.toast.warning.invalidFileType'));
        return;
      }

      onFileChange(f);

      await writeInfoLog(
        'CSV file selected',
        'Contacts.UploadCsvCard.handleFileSelected',
        { fileName: f.name, size: f.size, type: f.type }
      );
    },
    [onFileChange],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] ?? null;
      void handleFileSelected(f);
      e.target.value = '';
    },
    [handleFileSelected],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled || uploading) {
        return;
      }

      const f = e.dataTransfer.files?.[0] ?? null;
      void handleFileSelected(f);
    },
    [disabled, uploading, handleFileSelected],
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    await writeInfoLog(
      'Download contacts template requested',
      'Contacts.UploadCsvCard.handleDownloadTemplate',
      {}
    );
    try {
      onDownloadTemplate();
      await writeSuccessLog(
        'Template download triggered',
        'Contacts.UploadCsvCard.handleDownloadTemplate',
        {}
      );
    } catch (e) {
      await writeErrorLog(
        t('contacts.toast.error.downloadTemplate', { lng:'en' }),
        'Contacts.UploadCsvCard.handleDownloadTemplate',
        { error: String((e as Error)?.message ?? e) }
      );
      paintToast(LogLevels.ERROR, t('contacts.toast.error.dowloadTemplate'));
    }
  }, [onDownloadTemplate]);

  const handleUpload = useCallback(async () => {
    if (!canUpload) {
      await writeWarningLog(
        t('contacts.toast.warning.uploadBlocked', { lng: 'en' }),
        'Contacts.UploadCsvCard.handleUpload.validation',
        { listName: safeListName.trim(), hasFile: Boolean(file) }
      );
      paintToast(LogLevels.WARNING, t('contacts.toast.warning.uploadBlocked'));
      return;
    }

    await writeInfoLog(
      'Uploading CSV contacts...',
      'Contacts.UploadCsvCard.handleUpload',
      {
        listName: safeListName.trim(),
        fileName: file?.name ?? '',
        fileSize: file?.size ?? 0,
      }
    );

    try {
      await onUpload();
      await writeSuccessLog(
        t('contacts.toast.success.contactsUpload', { lng:'en' }),
        'Contacts.UploadCsvCard.handleUpload',
        { listName: safeListName.trim(), fileName: file?.name ?? '' }
      );
      paintToast(LogLevels.SUCCESS, t('contacts.toast.success.contactsUpload'));
    } catch (e) {
      await writeErrorLog(
        t('contacts.toast.error.csvUpload', { lng: 'en' }),
        'Contacts.UploadCsvCard.handleUpload',
        {
          listName: safeListName.trim(),
          fileName: file?.name ?? '',
          error: String((e as Error)?.message ?? e),
        }
      );
      paintToast(LogLevels.ERROR, t('contacts.toast.error.csvUpload'));
    }
  }, [canUpload, file, onUpload, safeListName]);

  return (
    <Card className="rounded-md border border-border bg-card text-foreground shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Upload CSV Contacts</CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* List name */}
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">List name</div>
          <Input
            value={safeListName}
            onChange={(e) => {
              onListNameChange(e.target.value);
            }}
            placeholder="My contact list"
            disabled={disabled || uploading}
          />
        </div>

        {/* Dropzone */}
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          role="button"
          tabIndex={0}
          onClick={handlePickFileClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handlePickFileClick();
            }
          }}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed p-4 text-center transition',
            'bg-background hover:bg-muted/30',
            (disabled || uploading) && 'cursor-not-allowed opacity-70',
          )}
        >
          <UploadCloud className="h-5 w-5 text-muted-foreground" />
          <div className="text-sm font-medium">
            {file !== null ? file.name : 'Drag & drop your CSV here'}
          </div>
          <div className="text-xs text-muted-foreground">
            {file !== null ? 'Click to change file' : 'or click to select file'}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onInputChange}
            disabled={disabled || uploading}
          />
        </div>

        {hint !== undefined && hint !== '' && (
          <div className="rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
            {hint}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="ghost"
          className="w-full justify-start gap-2 sm:w-auto"
          onClick={() => {
            void handleDownloadTemplate();
          }}
          disabled={disabled || uploading}
        >
          <FileText className="h-4 w-4" />
          Download template
        </Button>

        <div className="flex w-full gap-2 sm:w-auto">
          <Button
            type="button"
            variant="outline"
            className="w-1/2 gap-2 sm:w-auto"
            onClick={() => {
              onFileChange(null);
              void writeInfoLog('Cleared selected CSV file', 'Contacts.UploadCsvCard.clearFile', {});
            }}
            disabled={disabled || uploading || file === null}
          >
            <Download className="h-4 w-4" />
            Clear
          </Button>

          <Button
            type="button"
            className="w-1/2 gap-2 sm:w-auto"
            onClick={() => {
              void handleUpload();
            }}
            disabled={!canUpload}
          >
            {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload contacts
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
